import {getRelayEntry, subscribeRelayRegistry} from '@github-ui/ahp-relay/relay-registry'
import {isResultPresentationEnabled, observeResultPresentation} from '@github-ui/ahp-relay/relay-result-presentation'
import type {EstablishedAhpSessionIdentity} from '@github-ui/ahp-session-client/contracts'
import {copilotUiNavigationContext} from './copilot-ui-readiness'
import {isOneCopilotBrowserTelemetryPathExcluded} from './telemetry-gate'
import {createContext, useEffect, type ReactNode} from 'react'

import {type CopilotResultPresentationSelection, createResultPresentationSelection} from './copilot-result-presentation'

export interface CopilotResultPresentationRoot {
  matches(identity: EstablishedAhpSessionIdentity): boolean
  navigate(url: URL): void
  claim(
    selection: CopilotResultPresentationSelection,
    identity: EstablishedAhpSessionIdentity,
  ): (() => void) | undefined
  dispose(): void
  failed(): void
}

export const CopilotResultPresentationRootContext = createContext<CopilotResultPresentationRoot | undefined>(undefined)

const mountedRoots = new WeakMap<CopilotResultPresentationRoot, {count: number}>()

export function CopilotResultPresentationRootProvider({
  root,
  contextRoot = root,
  children,
}: {
  root: CopilotResultPresentationRoot | undefined
  contextRoot?: CopilotResultPresentationRoot
  children: ReactNode
}) {
  useEffect(() => {
    if (!root) return
    const mounted = mountedRoots.get(root) ?? {count: 0}
    mounted.count++
    mountedRoots.set(root, mounted)
    return () => {
      mounted.count--
      queueMicrotask(() => {
        if (mounted.count !== 0 || mountedRoots.get(root) !== mounted) return
        mountedRoots.delete(root)
        root.dispose()
      })
    }
  }, [root])
  return <CopilotResultPresentationRootContext value={contextRoot}>{children}</CopilotResultPresentationRootContext>
}

export function startCopilotResultPresentationRoot(name: string): CopilotResultPresentationRoot | undefined {
  if (
    !isResultPresentationEnabled() ||
    isOneCopilotBrowserTelemetryPathExcluded(window.location.pathname) ||
    !['copilot-agents', 'repo', 'repo-agents', 'copilot-chat-app'].includes(name)
  )
    return
  return observeResultPresentation(() => {
    let disposed = false
    let routeKey: string | undefined
    let generation = 0
    let taskId: string | undefined
    let rootSelection: CopilotResultPresentationSelection | undefined
    let unmount: (() => void) | undefined
    let unsubscribe: () => void = () => undefined
    let owner: CopilotResultPresentationSelection | undefined
    let identity: EstablishedAhpSessionIdentity | undefined
    const clear = () => {
      unsubscribe()
      unsubscribe = () => undefined
      const previous = rootSelection?.getSnapshot()
      rootSelection?.select(undefined)
      if (previous && owner?.getSnapshot() === previous) owner.select(undefined)
      owner = undefined
      identity = undefined
      taskId = undefined
      unmount?.()
      unmount = undefined
      rootSelection = undefined
    }
    const navigate = (url: URL) => {
      if (disposed) return
      const navigation = copilotUiNavigationContext(url)
      const nextRouteKey = navigation ? JSON.stringify([navigation.surface, navigation.expectedTaskId]) : 'unsupported'
      if (routeKey === nextRouteKey) return
      routeKey = nextRouteKey
      const currentGeneration = ++generation
      clear()
      if (!navigation) return
      const selection = createResultPresentationSelection(navigation.surface)
      rootSelection = selection
      unmount = selection.mount()
      taskId = navigation.expectedTaskId
      if (!taskId) return
      const selectedTaskId = taskId
      const refresh = () => {
        if (disposed || generation !== currentGeneration || rootSelection !== selection) return
        const entry = getRelayEntry(selectedTaskId)
        if (!entry) return
        identity = {
          taskId: selectedTaskId,
          sessionId: entry.sessionId,
          environmentId: entry.environmentId,
          channel: entry.channel,
        }
        selection.select(identity)
        owner?.adopt(identity, selection.getSnapshot())
      }
      unsubscribe = subscribeRelayRegistry(() => observeResultPresentation(refresh))
      refresh()
    }
    const matches = (selected: EstablishedAhpSessionIdentity) => taskId === selected.taskId
    navigate(new URL(window.location.href, window.location.origin))
    return {
      matches,
      failed: () => rootSelection?.getSnapshot()?.renderFailed(),
      navigate: url => observeResultPresentation(() => navigate(url)),
      claim(selection, selected) {
        const selectedRoot = rootSelection
        const claimGeneration = generation
        if (!selectedRoot || selected.taskId !== taskId || !matches(selected) || (owner && owner !== selection)) return
        if (
          identity &&
          (selected.sessionId !== identity.sessionId || selected.environmentId !== identity.environmentId)
        )
          return
        owner = selection
        selectedRoot.select(selected)
        selection.adopt(selected, selectedRoot.getSnapshot())
        return () => {
          // StrictMode replays effects without replacing the intended view.
          queueMicrotask(() => {
            if (
              generation === claimGeneration &&
              rootSelection === selectedRoot &&
              owner === selection &&
              !selection.getSnapshot()
            ) {
              owner = undefined
            }
          })
        }
      },
      dispose() {
        if (disposed) return
        disposed = true
        generation++
        clear()
      },
    }
  })
}
